import type { EntityState, PickUpRefusalReason, Rules } from "odyssey-shared";
import { gameReport } from "../report.ts";
import { AutoLoot, pageAutoLoot } from "./auto-loot.ts";
import { feetJumped, loudestLanding } from "./landing-sound.ts";
import { GroundMarks } from "./marks.ts";
import { GroundPickUp, type PickUpHost } from "./pick-up.ts";
import type { GroundView } from "./view.ts";

/**
 * The page's ground items for one session (P2.8-T65): the marks of its own items from `lootGained` beside the record's
 * own state 2 (owned by you) for the items reserved for its character (P2.8-T81, `GroundMarks.owns`), the view that
 * draws every item in the client's view, the pick-up the interact key and a click ask, and the one landing sound a
 * drawing plays for the high tier items the world dropped that it saw land (P2.8-T77, `landing-sound.ts`). The session hands it each
 * applied snapshot, each `lootGained`'s `grounded` and each `pickUpRefused`, and clears it when the session ends.
 */
export class PageGround {
  private readonly marks = new GroundMarks();
  private readonly autoLoot: AutoLoot;
  private readonly lastFeet = { x: 0, y: 0 };
  private hadFeet = false;

  /**
   * The page's pick-up, which E asks when no thing is its target and a click asks first.
   */
  public readonly pickUp: GroundPickUp;

  /**
   * @param host What the pick-up reads of the session, the feet a landing sound is placed from among it.
   * @param view The world's ground view, null before the world has loaded.
   * @param rules The page's rules, which name each item's rarity.
   * @param landed Plays a landing sound: its hook and the item's offset from the feet in tiles; none for a page with
   *   no sound.
   * @param autoLoot Whether Auto loot is on now (P2.8-T79); the page's choice unless given.
   */
  public constructor(
    private readonly host: PickUpHost,
    private readonly view: () => GroundView | null,
    private readonly rules: Rules | null = null,
    private readonly landed: ((hook: string, dx: number, dy: number) => void) | null = null,
    autoLoot: () => boolean = () => pageAutoLoot.on,
  ) {
    this.autoLoot = new AutoLoot(autoLoot);
    this.pickUp = new GroundPickUp(host);
  }

  /**
   * Draws the items an applied snapshot leaves in view, after ending the marks it no longer bears out, starting the
   * landings' settling again first when the character's feet jumped since the last snapshot (`feetJumped`).
   *
   * @param entities The client's view.
   */
  public snapshot(entities: ReadonlyMap<number, EntityState>): void {
    this.marks.settle(entities);
    const feet = this.host.feet();
    if (feetJumped(this.hadFeet ? this.lastFeet : undefined, feet)) {
      this.view()?.resettle();
    }
    if (feet !== undefined) {
      this.lastFeet.x = feet.x;
      this.lastFeet.y = feet.y;
    }
    this.hadFeet = feet !== undefined;
    this.draw(entities);
    const asked = this.autoLoot.next(entities, (handle) => {
      const state = entities.get(handle);
      return state !== undefined && this.marks.owns(state);
    }, feet);
    if (asked >= 0) {
      this.pickUp.ask(asked);
    }
  }

  /**
   * Marks the items a `lootGained` put on the ground for the page's character, and draws them marked if they are
   * already in view.
   *
   * @param grounded The message's `grounded`.
   * @param entities The client's view, null outside a connection.
   */
  public gained(grounded: readonly (readonly [handle: number, item: string, quantity: number])[], entities: ReadonlyMap<number, EntityState> | null): void {
    if (grounded.length === 0) {
      return;
    }
    this.marks.mark(grounded);
    if (entities !== null) {
      this.draw(entities);
    }
  }

  /**
   * Shows a refused pick-up, unless it answers Auto loot's request, which stays quiet (P2.8-T79).
   *
   * @param reason The server's reason.
   * @param waitMs For `owned`, the server's time until the item turns public.
   * @param handle The refused item's handle, as sent.
   */
  public refused(reason: PickUpRefusalReason, waitMs: number, handle = -1): void {
    if (!this.autoLoot.refused(handle, reason)) {
      this.pickUp.refused(reason, waitMs);
    }
  }

  /**
   * Forgets the marks and hides every item, as a session ends.
   */
  public clear(): void {
    this.marks.clear();
    this.autoLoot.clear();
    this.hadFeet = false;
    this.view()?.clear();
    gameReport.ground = [];
  }

  /**
   * Draws the items in view and lists them for the report when they were placed again, and plays the drawing's landing
   * sound, if any.
   *
   * @param entities The client's view.
   */
  private draw(entities: ReadonlyMap<number, EntityState>): void {
    const view = this.view();
    if (view === null || !view.sync(entities, this.marks)) {
      return;
    }
    gameReport.ground = view.readings;
    if (view.landedCount > 0 && this.landed !== null && this.rules !== null) {
      const sound = loudestLanding(view.landed, view.landedCount, entities, this.rules, this.host.feet());
      if (sound !== null) {
        this.landed(sound.hook, sound.dx, sound.dy);
      }
    }
  }
}
